"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
const COURSE_OPTIONS = ["Técnico Electricista", "Barbería", "Kinesiología Deportiva", "Masoterapia", "Técnico en Aires Acondicionados", "Supervisor Forestal", "Técnico en Electrónica", "Técnico en Energía Solar", "Presupuestación de Obras", "Producción Avícola", "Extensiones de Pestañas", "Primeros Auxilios", "Reflexología Podal", "Auriculoterapia", "Entrenador Integral", "Entrenamiento Funcional", "Instructor en Sala de Musculación", "Auxiliar Administrativo", "Legislación Laboral y Liquidación de Sueldos", "Excel", "Conducción, Locución y Streaming"];
function IniciaForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-green-500/20 p-4 rounded-full mb-6">
          <CircleCheck className="size-12 text-green-400" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-4">¡Solicitud Enviada!</h3>
        <p className="text-slate-300 mb-8 max-w-md mx-auto">{successMessage}</p>
        <Button asChild variant="outline" className="rounded-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
          <Link to="https://wa.me/59896344395" newTab>
            Contactar por WhatsApp
          </Link>
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200">Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Juan Pérez" className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12" {...field} />
              </FormControl>
              <FormDescription className="text-slate-400 text-xs">
                Ingresa tu nombre tal como deseas que aparezca en la comunicación.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ingresa un correo válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200">Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan@example.com" className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12" {...field} />
              </FormControl>
              <FormDescription className="text-slate-400 text-xs">
                Te enviaremos información sobre los cursos y promociones.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200">Teléfono de Contacto</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+598 99 123 456" className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 rounded-xl h-12" {...field} />
              </FormControl>
              <FormDescription className="text-slate-400 text-xs">
                Opcional, pero nos permite responder más rápido.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="course_interest" rules={{
        required: "Por favor selecciona un curso"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-200">Curso de Interés</FormLabel>
              <FormControl>
                <select {...field} className="flex h-12 w-full items-center justify-between rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-2 text-base text-white ring-offset-background placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none">
                  <option value="" disabled className="text-slate-500">Selecciona un curso</option>
                  {COURSE_OPTIONS.map((opt, i) => <option key={i} value={opt} data-index={i} className="bg-slate-800 text-white">
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormDescription className="text-slate-400 text-xs">
                Elige el curso que más te interese para recibir información detallada.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-slate-200">Comentario o Pregunta</FormLabel>
              <FormControl>
                <textarea {...field} placeholder="Escribe aquí cualquier consulta adicional..." className="flex min-h-[120px] w-full rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-3 text-base text-white ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-xl flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive-foreground text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-14 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg font-semibold shadow-lg shadow-secondary/20 transition-all hover:scale-[1.02]">
          {isSubmitting && <Loader className="animate-spin mr-2 size-5" />}
          Inscribirme ahora
        </Button>
      </div>
    </Form>;
}
export default function IniciaTuFormacion() {
  return <section id="inicia-tu-formación" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="bg-foreground rounded-[2.5rem] p-8 md:p-12 lg:p-16 shadow-2xl relative overflow-hidden">
          {}
          <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative z-10">
            <div className="max-w-2xl mb-10">
              <span className="inline-block py-1 px-3 rounded-full bg-primary/20 text-primary-foreground text-sm font-medium mb-4 border border-primary/30">
                Inicia tu Formación
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background tracking-tight mb-4">
                ¿Listo para el siguiente paso?
              </h2>
              <p className="text-slate-300 text-lg">
                Completa el formulario a continuación y da el primer paso hacia tu nueva carrera profesional con ECAP.
              </p>
            </div>

            <FormProvider formId="inicia_formacion_form_home" sectionName="Inicia tu Formación" defaultValues={{
            name: "",
            email: "",
            phone: "",
            course_interest: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            course_interest: {
              required: true
            }
          }} submitText="Inscribirme" successMessage="¡Gracias por tu interés! Hemos recibido tus datos y nos pondremos en contacto contigo a la brevedad para brindarte toda la información.">
              <IniciaForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}